import { createClient } from '@supabase/supabase-js';

function normalizeProjectUrl(value) {
  let url = String(value || '').trim().replace(/\/$/, '');
  ['/rest/v1', '/auth/v1', '/storage/v1'].forEach((suffix) => {
    if (url.endsWith(suffix)) url = url.slice(0, -suffix.length);
  });
  return url.replace(/\/$/, '');
}

const supabaseUrl = normalizeProjectUrl(process.env.REACT_APP_SUPABASE_URL);
const publishableKey = String(process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || '').trim();

if (!supabaseUrl || !publishableKey) {
  // Fail clearly during startup without ever logging either configured value.
  throw new Error('Supabase frontend configuration is missing.');
}

export const supabase = createClient(supabaseUrl, publishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

let cachedSession = null;
let cachedAuthEvent = '';
let passwordRecoveryActive = false;

supabase.auth.getSession().then(({ data }) => {
  cachedSession = data.session || null;
});

supabase.auth.onAuthStateChange((event, session) => {
  cachedSession = session || null;
  cachedAuthEvent = event || cachedAuthEvent;
  if (event === 'PASSWORD_RECOVERY') passwordRecoveryActive = true;
  if (event === 'SIGNED_OUT') passwordRecoveryActive = false;
});

export function toAppUser(user) {
  if (!user) return null;
  const metadata = user.user_metadata || {};
  return {
    id: user.id,
    email: user.email || '',
    phone: user.phone || '',
    name: metadata.full_name || metadata.name || (user.email || '').split('@')[0] || '',
    company: metadata.company_name || '',
    provider: user.app_metadata?.provider || user.identities?.[0]?.provider || 'email',
    created_at: user.created_at,
  };
}

export function getCachedAccessToken() {
  return cachedSession?.access_token || '';
}

export async function getAccessToken() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  cachedSession = data.session || null;
  return cachedSession?.access_token || '';
}

export async function getInitialAuthState() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  cachedSession = data.session || null;
  return {
    session: cachedSession,
    user: toAppUser(cachedSession?.user),
    event: cachedAuthEvent,
    passwordRecovery: passwordRecoveryActive,
  };
}

export function subscribeToAuthChanges(callback) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    cachedSession = session || null;
    callback({ event, session: cachedSession, user: toAppUser(cachedSession?.user) });
  });
  return () => data.subscription.unsubscribe();
}

export async function signUpWithEmail({ email, password, name, company }) {
  const redirectTo = `${window.location.origin}/`;
  const acceptedAt = new Date().toISOString();
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectTo,
      data: {
        full_name: name,
        company_name: company || '',
        terms_accepted_at: acceptedAt,
        terms_version: '2026-07-24',
        privacy_notice_version: '2026-07-24',
      },
    },
  });
  if (error) throw error;
  return { session: data.session, user: toAppUser(data.user) };
}

export async function signInWithEmail({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return { session: data.session, user: toAppUser(data.user) };
}

export async function signInWithGoogle() {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/` },
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  cachedSession = null;
}

export async function clearLocalSession() {
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  cachedSession = null;
  if (error) throw error;
}

export async function requestPasswordReset(email) {
  const redirectTo = `${window.location.origin}/?reset-password=1`;
  const { data, error } = await supabase.auth.resetPasswordForEmail(String(email || '').trim(), { redirectTo });
  if (error) throw error;
  return data;
}

export async function updatePassword(password) {
  if (!passwordRecoveryActive) {
    throw new Error('The password-reset link is invalid or expired. Request a new link.');
  }
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.user) {
    throw new Error('The password-reset link is invalid or expired. Request a new link.');
  }
  const { data, error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
  passwordRecoveryActive = false;
  return { user: toAppUser(data.user) };
}

export async function changePassword(currentPassword, newPassword) {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  const sessionUser = sessionData?.session?.user;
  if (sessionError || !sessionUser?.id || !sessionUser.email) {
    throw new Error('Your session expired. Sign in again before changing your password.');
  }

  const { data: verified, error: verificationError } = await supabase.auth.signInWithPassword({
    email: sessionUser.email,
    password: currentPassword,
  });
  if (verificationError || verified.user?.id !== sessionUser.id) {
    throw new Error('The current password is incorrect.');
  }

  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return { user: toAppUser(data.user) };
}
